import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Shield, Loader2 } from "lucide-react";

interface TermsSectionProps {
  form: UseFormReturn<any>;
  isSubmitting: boolean;
}

export const MembershipTermsSection = ({ form, isSubmitting }: TermsSectionProps) => {
  return (
    <Card className="form-section-card animate-fade-in hover-scale">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          Terms and Conditions
        </CardTitle>
        <CardDescription>
          Please review and agree to the by-laws and terms of the Sacco.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="prose prose-sm max-w-none">
          <p className="text-foreground mb-4">
            The relationship between a member and NATCON Sacco is governed by its by-laws and any written agreements.
          </p>
          
          <h4 className="font-semibold text-foreground mb-3">Key points of membership include:</h4>
          
          <div className="space-y-4 text-sm text-foreground/90">
            <div className="border-l-4 border-primary pl-4">
              <h5 className="font-medium text-foreground">Eligibility</h5>
              <p>To be eligible, you must be a Kenyan resident, at least 18 years old, of good character, and an employee or retired employee of the National Construction Authority.</p>
            </div>
            
            <div className="border-l-4 border-primary pl-4">
              <h5 className="font-medium text-foreground">Admission</h5>
              <p>Admission is by a majority vote of the Management Committee and requires a Ksh. 1,500 application fee and a minimum of 250 shares at Ksh. 20 each. The minimum monthly contribution is Ksh. 2,000.</p>
            </div>
            
            <div className="border-l-4 border-destructive pl-4">
              <h5 className="font-medium text-foreground">Non-Refundable Shares</h5>
              <p>Shares are non-refundable.</p>
            </div>
            
            <div className="border-l-4 border-amber-500 pl-4">
              <h5 className="font-medium text-foreground">Withdrawal</h5>
              <p>A 60-day written notice is required. All outstanding loans must be cleared before withdrawal. Re-admission requires a Ksh. 1,000 fine and six months of consecutive savings.</p>
            </div>
            
            <div className="border-l-4 border-destructive pl-4">
              <h5 className="font-medium text-foreground">Expulsion</h5>
              <p>Grounds for expulsion include failure to pay contributions for six consecutive months, dishonest or fraudulent actions, or membership in another similar society.</p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-accent/20 rounded-lg border border-accent">
            <p className="text-sm font-medium italic text-foreground">
              I hereby confirm that I have read, understood and complied with all the membership terms and conditions as contained in the by-laws and the particulars I have given are true to the best of my belief.
            </p>
          </div>
        </div>
        
        <FormField
          control={form.control}
          name="termsAgreed"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="mt-1"
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-medium cursor-pointer">
                  I agree to the terms and conditions. *
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        
        <div className="pt-6 border-t">
          <Button 
            type="submit" 
            size="lg" 
            disabled={isSubmitting}
            className="w-full submit-button hover-scale font-semibold py-6 text-lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Submitting Application...
              </>
            ) : (
              'Submit Membership Application'
            )}
          </Button>
          
          <p className="text-xs text-muted-foreground text-center mt-4">
            By submitting this application, you acknowledge that all information provided is accurate and complete.
            Your application will be reviewed by the Management Committee.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};